import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import FoundingMemberBadge from '@/components/ui/FoundingMemberBadge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";

const profileSchema = z.object({
  full_name: z.string().min(1, 'Full name is required').max(100, 'Name too long'),
});

type ProfileFormData = z.infer<typeof profileSchema>;

const changePasswordSchema = z.object({
  newPassword: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmNewPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmNewPassword, {
  message: "Passwords don't match",
  path: ["confirmNewPassword"],
});

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

export default function AccountProfileTab() {
  const { profile, user, isFoundingMember } = useAuth();
  const queryClient = useQueryClient();
  const [isPasswordChangeModalOpen, setIsPasswordChangeModalOpen] = useState(false);

  const { 
    register: registerProfile, 
    handleSubmit: handleProfileSubmit, 
    formState: { errors: profileErrors, isSubmitting: isProfileSubmitting, isDirty: isProfileDirty }, 
    reset: resetProfileForm 
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      full_name: profile?.full_name || '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordFormSubmit,
    formState: { errors: passwordErrors, isSubmitting: isPasswordSubmitting },
    reset: resetPasswordForm,
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  });

  React.useEffect(() => {
    if (profile && profile.full_name !== undefined) {
        resetProfileForm({ full_name: profile.full_name || '' });
    }
  }, [profile, resetProfileForm]);

  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      if (!profile?.user_id) throw new Error("User ID not found");

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ full_name: data.full_name, updated_at: new Date().toISOString() })
        .eq('user_id', profile.user_id)
        .select()
        .single();

      if (updateError) {
        console.error("Supabase Profile Update Error:", updateError);
        throw new Error(updateError.message || 'Database error updating profile.');
      }
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      resetProfileForm({ full_name: data.full_name }, { keepValues: false });
      toast.success('Profile updated successfully!');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update profile: ${error.message}`);
    },
  });

  const onProfileSubmit = (data: ProfileFormData) => {
    updateProfileMutation.mutate(data);
  };

  const changePasswordMutation = useMutation({
    mutationFn: async (data: ChangePasswordFormData) => {
      const { error } = await supabase.auth.updateUser({
        password: data.newPassword,
      });
      if (error) {
        console.error("Supabase password update error:", error);
        throw error;
      }
      return { message: "Password updated successfully." };
    },
    onSuccess: (data) => {
      toast.success(data.message);
      resetPasswordForm();
      setIsPasswordChangeModalOpen(false);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update password: ${error.message}`);
    },
  });

  const onPasswordChangeSubmit = (data: ChangePasswordFormData) => {
    changePasswordMutation.mutate(data);
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-x-3 mb-6">
        <h2 className="text-xl font-semibold text-slate-gray">Your Profile</h2>
        {isFoundingMember && <FoundingMemberBadge />}
      </div>

      <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="full_name">Full Name</Label>
          <Input
            id="full_name"
            {...registerProfile('full_name')}
            className={profileErrors.full_name ? 'border-red-500' : ''}
            placeholder="Enter your full name"
          />
          {profileErrors.full_name && (
            <p className="text-red-500 text-sm">{profileErrors.full_name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email Address (Login)</Label>
          <Input
            id="email"
            type="email"
            value={user?.email || profile?.email || 'Loading...'}
            disabled
            readOnly
            className="bg-gray-100 cursor-not-allowed border-gray-300"
          />
          <p className="text-xs text-slate-gray">
            Email cannot be changed here. Please contact support if needed.
          </p>
        </div>

        <Button
          type="submit"
          disabled={isProfileSubmitting || updateProfileMutation.isPending || !isProfileDirty}
          className="w-full sm:w-auto"
        >
          {(isProfileSubmitting || updateProfileMutation.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {(isProfileSubmitting || updateProfileMutation.isPending) ? 'Saving...' : 'Save Profile Changes'}
        </Button>
        {!isProfileDirty && !(isProfileSubmitting || updateProfileMutation.isPending) && (
          <span className="text-sm text-gray-500 ml-4">No changes to save.</span>
        )}
      </form>

      <div className="mt-8 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-medium text-slate-gray mb-2">Password</h3>
        <p className="text-sm text-slate-gray mb-4">
          You can change your password directly here.
        </p>
        <Dialog open={isPasswordChangeModalOpen} onOpenChange={setIsPasswordChangeModalOpen}>
          <DialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
            >
              Change Password
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
              <DialogDescription>
                Enter your new password below. Make sure it's secure.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handlePasswordFormSubmit(onPasswordChangeSubmit)} className="space-y-4 py-4">
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  {...registerPassword('newPassword')}
                  className={passwordErrors.newPassword ? 'border-red-500' : ''}
                />
                {passwordErrors.newPassword && (
                  <p className="text-red-500 text-sm mt-1">{passwordErrors.newPassword.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
                <Input
                  id="confirmNewPassword"
                  type="password"
                  {...registerPassword('confirmNewPassword')}
                  className={passwordErrors.confirmNewPassword ? 'border-red-500' : ''}
                />
                {passwordErrors.confirmNewPassword && (
                  <p className="text-red-500 text-sm mt-1">{passwordErrors.confirmNewPassword.message}</p>
                )}
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline" onClick={() => resetPasswordForm()}>
                    Cancel
                  </Button>
                </DialogClose>
                <Button type="submit" disabled={isPasswordSubmitting || changePasswordMutation.isPending}>
                  {(isPasswordSubmitting || changePasswordMutation.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save New Password
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}